import { createApp } from 'vue';
import { PiniaColada } from '@pinia/colada';
import { getPinia } from '@/createPiniaSingleton';
import { getVuetify } from '@/createVueIsland';
import axios from 'axios';
import { getConfig } from '@/services/utils';
import VueLuzmoDashboard from '@luzmo/vue-embed';
import { companyContextService } from '@/services/CompanyContextService';
import { sentryService } from '@/services/SentryService';
import { handleUnauthorized } from '@/unauthorizedResponse';

// Initialize company context service for mismatch detection
// This should be done early in the app lifecycle
let contextInitialized = false;
const initializeCompanyContext = async () => {
  if (!contextInitialized && window.leanlaw && window.leanlaw.domain) {
    try {
      await companyContextService.initializeContext();
      contextInitialized = true;
      console.log('[App] Company context service initialized');
    } catch (error) {
      console.warn('[App] Company context initialization failed:', error);
    }
  }
};

// Global company context mismatch modal is mounted by companyContextModal.js,
// which is imported from app.js and therefore runs on every page.

axios.interceptors.request.use(config => {
  config = getConfig(config);
  return config;
}, error => {
  console.log(error);
  Promise.reject(error);
});
axios.interceptors.response.use(response => {
  if (response.config.url.indexOf('/health') > 0) {
    return response;
  }
  return response;
}, error => {
  if (error.response?.status === 401) {
    const isAuthCheck = (error.config?.url?.indexOf('/health') ?? -1) > 0;
    return handleUnauthorized(error.response.data, {
      isAuthCheck
    }).then(handled => handled ? new Promise(() => {}) : Promise.reject(error));
  }

  // Handle company context mismatch (409 Conflict)
  if (error.response && error.response.status === 409) {
    const data = error.response.data;
    if (data && data.error === 'COMPANY_CONTEXT_MISMATCH') {
      // Trigger company context mismatch handling
      // Use custom event picked up by companyContextModal.js
      const mismatchEvent = new CustomEvent('companyContextMismatch', {
        detail: {
          message: data.message,
          pageCompanyId: error.config?.headers['x-leanlaw-companyid'],
          requestUrl: error.config?.url,
          timestamp: new Date().toISOString()
        }
      });
      window.dispatchEvent(mismatchEvent);

      // Return a never-resolving promise so the error does NOT propagate
      // to downstream .catch() handlers (which would show a duplicate toast).
      return new Promise(() => {});
    }
  }
  return Promise.reject(error);
});
const vuetify = getVuetify();

// Id helper - Always generate unique IDs to avoid stale element selection during state transitions
function resolveId(root, provided) {
  if (provided) return provided;
  // DO NOT use root.name as the sole ID—it's non-unique and causes stale element selection
  // during rapid state transitions (e.g., Client A → Client B). Always append a counter.
  if (!window.llVueIdCounter) window.llVueIdCounter = 1;
  const uniqueId = `ll-vue-${window.llVueIdCounter++}`;
  return uniqueId;
}
const sharedPinia = getPinia();

// Note: Sentry is initialized in tracking.js via the Angular tracking factory.
// It handles DSN, environment detection, browser tracing, replay integration, and user context.
// Vue components use the useSentry() composable which imports the singleton directly (no provide/inject).

export default (root, id, uiView = root.uiView) => {
  const resolved = resolveId(root, id);
  return {
    template: uiView ? `<div id="${resolved}"></div><div ui-view="${uiView}"></div>` : `<div id="${resolved}"></div>`,
    controller: ['iocContainer', '$scope', function (container, $scope) {
      return (() => {
        if (!window.llVueRegistry) window.llVueRegistry = new WeakMap();
        const host = document.getElementById(resolved);
        const record = host && window.llVueRegistry.get(host);
        // Unmount any existing Vue app mounted to this host element
        // This handles cases where the controller is re-initialized (e.g., page refresh, direct navigation)
        if (record && record.app) {
          try {
            record.app.unmount();
          } catch (e) {
            /* ignore */
          }
        }
        const app = createApp(root, container).use(sharedPinia).use(PiniaColada).use(vuetify).use(VueLuzmoDashboard);

        // Initialize company context for mismatch detection
        initializeCompanyContext();

        // Register this Vue app with Sentry so @sentry/vue captures
        // Vue-specific errors (component name, lifecycle hook, etc.)
        sentryService.attachVueApp(app);

        // CRITICAL: Unmount Vue app when Angular destroys the controller/view
        // This handles named views that don't trigger controller re-initialization
        $scope.$on('$destroy', () => {
          if (app) {
            try {
              app.unmount();
              // Clean up registry entry
              if (host && window.llVueRegistry.has(host)) {
                window.llVueRegistry.delete(host);
              }
            } catch (e) {
              console.warn('[Vue/Angular Bridge] Error during $destroy unmount:', e);
            }
          }
        });
        app.mixin({
          props: {
            leanLawApi: {
              type: Object,
              required: false
            },
            userService: {
              type: Object,
              required: false
            },
            alerts: {
              type: Object,
              required: false
            },
            modalService: {
              type: Object,
              required: false
            }
          },
          computed: {
            formatCurrencyFilter() {
              const numberFormat = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
              });
              return currency => numberFormat.format(currency);
            },
            formatDateFilter() {
              const dateTimeFormat = new Intl.DateTimeFormat('default', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit'
              });
              return date => dateTimeFormat.format(date);
            }
          },
          methods: {
            formatCurrency(value) {
              if (!value) {
                return '$0.00';
              }
              return this.formatCurrencyFilter(value);
            },
            amountNoZero(amount) {
              if (!amount) {
                return '-';
              }
              if (amount < 0) {
                return `(${this.formatCurrencyFilter(-amount)})`;
              }
              return this.formatCurrencyFilter(amount);
            },
            amount(amount) {
              if (!amount) {
                return '';
              }
              if (amount < 0) {
                return `(${this.formatCurrencyFilter(-amount)})`;
              }
              return this.formatCurrencyFilter(amount);
            },
            formatDate(dateString) {
              if (!dateString) return '';
              const date = new Date(dateString);
              return this.formatDateFilter(date);
            },
            hoursDecimal(input, showZero) {
              if (!input && showZero === false) return '';
              const val = parseFloat(input);
              if (Number.isNaN(val)) return input;
              // Determine if the value has a hundredths place
              const hasHundredths = Math.round(val * 100) % 10 !== 0;
              if (hasHundredths) return val.toFixed(2);
              return val.toFixed(1);
            }
          }
        });
        const vm = app.mount(`#${resolved}`);
        if (host) window.llVueRegistry.set(host, {
          app,
          vm
        });
        return vm;
      })();
    }]
  };
};